import React, { useEffect, useState, useRef } from 'react'
import Chart, { Grid as GridChart, CommonSeriesSettings, ArgumentAxis, Series, Legend, ValueAxis, ZoomAndPan, ScrollBar, Tooltip, TickInterval, Aggregation, Point, Title, AggregationInterval, Label, Height, Crosshair, Font, MinorTickInterval, Tick, MinorTick, Strip, CommonAxisSettings } from 'devextreme-react/chart';
import { useMobileLandscape, useTabletContext } from '../../Context/ContextProvider'
import Lottie from 'lottie-react'
import rotateScreenAnimation from '../../assets/rotate-phone.json'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import {
    MultiLoggerColumnData,
    loggerInfo,
    APISiteLoggerDetail,
    reloadData,
    AvgStyling,
    handleCustomColorLoad,
    professionalColors
} from './MinimumNightFlowFunctions'
import { adjustTime } from '../../Utils/AdjustTime'
import moment, { Moment } from 'moment'
import { VisualRange } from 'devextreme-react/common/charts';
import Loader from '../../Utils/Loader/NewLoader2025'
import { CallGetAPI, CallPostAPI, CreateUrl } from '../../Utils/ApiHelper';
import { UserSettings } from '../../models/APIUserSettingsModel'
import { Ashridge } from '../../assets/jss/material-dashboard-react'
import FullWidthMobileButton from '../../components/FullWidthMobileButton';
import SlideInAnimation from '../../Animation Wrappers/SlideInAnimation';
import DateRange from '../../components/Dates/DateRange';

interface Location {
    hash: string
    key: string
    pathname: string
    search: string
    state: RedirectProps
}

interface Props {
    location: Location
    history: {
        goBack: () => void;
    };
}

interface RedirectProps {
    serial: number | null
    siteId: number | null
    loggerData: Array<APISiteLoggerDetail> | null
    startDateProps: Date
    endDateProps: Date
    showDateLink: boolean
}


const MinimumNightFlowMobile: React.FC<Props> = ({location, history }) => {


    ///hooks / context
    const isLandscape:boolean = useMobileLandscape()
    const isTablet:boolean = useTabletContext()
    const intl: IntlShape = useIntl()



    /// refs
    const chartRef = useRef(null)

    const [loading, setLoading] = useState<boolean>(false)
    const [startDate, setStartDate] = useState<Date>(adjustTime(moment().subtract(3, 'months').set({ hour: 0, minute: 0, second: 0 }).toDate()))
    const [endDate, setEndDate] = useState<Date>(adjustTime(moment().set({ hour: 23, minute: 59, second: 59 }).toDate()))
    const [allValuesZero, setAllValuesZero] = useState<boolean>(true)
    const [loggers, setLoggers] = useState<Array<loggerInfo>>([])
    const [useCustomColors, setUseCustomColors] = useState<boolean>(false)
    const [graphData, setGraphData] = useState<Array<MultiLoggerColumnData>>([])
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [outputLoggers, setOutputLoggers] = useState<Array<number>>([])
    const [visualRange, setVisualRange] = useState<VisualRange>({ startValue: startDate, endValue: endDate })
    const [averageStyling, setAverageStyling] = useState<AvgStyling>({ color: '#FF69B4', width: 2, showPoints: true, size: 5 })
    const [showDatePicker, setShowDatePicker] = useState<boolean>(false)
    const [showShowHide, setShowShowHide] = useState<boolean>(false)
    const [dateChange, setDateChange] = useState<boolean>(false)
    const [shouldReloadData, setShouldReloadData] = useState<boolean>(false)
    const [showHideDisplayLoggers, setShowHideDisplayLoggers] = useState<number[]>([])
    const [showAvg, setShowAvg] = useState<boolean>(true)

   
    useEffect(() => {
        reloadData(
            setLoading,
            location.state.serial,
            startDate,
            endDate,
            setAllValuesZero,
            loggers,
            useCustomColors,
            setUseCustomColors,
            setLoggers,
            setGraphData,
            location.state.siteId,
            location.state.loggerData,
            setErrorMessage,
            setOutputLoggers,
            intl
        )
    }, [])


    useEffect(() => {
        CallGetAPI(CreateUrl(`/api/aquaguard/usersettings?companyId=${sessionStorage.getItem('companyId')}&userId=${sessionStorage.getItem('userId')}&settingsType=MNF`), {})
            .then((data: UserSettings[]) => {
                for (const userSetting of data) {
                    if (userSetting.settingsKey === 'Custom Average') {
                        const avgSett: AvgStyling = JSON.parse(userSetting.settingsValue)
                        avgSett.color = `#${avgSett.color}`
                        setAverageStyling(avgSett)
                    }
                }
            })
    }, [graphData])


    


    const renderLoggerSeries = (logger: loggerInfo): Array<React.ReactNode> => {

        const loggerSeries = []
        loggerSeries.push(<Series
            valueField={logger.serial.toString()}
            color={logger.color}
            name={logger.serial}
            type="stackedbar"
        />)

        return loggerSeries
    }




    const renderToolTip = (point, intl: IntlShape): React.ReactNode => {
        return (
            <div>
                <p>{`${intl.formatMessage({ id: 'Bar/Line' })}: ${point.seriesName}`}</p>
                <p>{`${intl.formatMessage({ id: 'Value' })}: ${point.originalValue} l/s`}</p>
                <p>{`${intl.formatMessage({ id: 'Time of minimum flow' })}: ${moment(point.point.data.timeOfReading).format("DD-MM-yyyy hh:mm:ss A")}`}</p>
            </div>
        )
    }


    const toggleDatePicker = (): void => {
        setShowDatePicker(!showDatePicker)
        setShowShowHide(false)
    }

    const toggleShowHideClick = (): void => {
        setShowShowHide(!showShowHide)
        setShowDatePicker(false)
    }


    const onDateChange = (startDateChange: Moment, endDateChange: Moment, rainfall, recent): void => {

        let finalDate = moment(endDateChange).toDate() >= moment().toDate() ? moment(endDateChange).set({ hour: 0, minute: 0, second: 0 }).toDate() : moment(endDateChange).set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).toDate()
        if (finalDate > moment().toDate()) {
            finalDate = moment().set({ hour: 0, minute: 0, second: 0 }).toDate()
        }
        const beginDate = moment(startDateChange).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toDate()
        setStartDate(beginDate)
        setEndDate(finalDate)
        if (recent) {
            setShouldReloadData(true)
        } else {
            setDateChange(true)
        }
    }

    const handleRedraw = (): void => {

        setDateChange(false)
        reloadData(
            setLoading,
            location.state.serial,
            startDate,
            endDate,
            setAllValuesZero,
            loggers,
            useCustomColors,
            setUseCustomColors,
            setLoggers,
            setGraphData,
            location.state.siteId,
            location.state.loggerData,
            setErrorMessage,
            setOutputLoggers,
            intl
        )
        setVisualRange({ startValue: startDate, endValue: endDate })
        setShowDatePicker(!showDatePicker)
    }

    const handleShowHide = (log:string):void => {
        if (log === 'avg') {
            setShowAvg(!showAvg)
        } else {
            let loggersTemp = [...loggers]
            if (loggersTemp.map(l => l.serial.toString()).includes(log)) {
                loggersTemp = loggersTemp.filter(l => l.serial.toString() !== log)
                setLoggers(loggersTemp)
            } else {
                loggersTemp.push({ serial: parseInt(log), color: professionalColors[0]})

                if (useCustomColors) {
                    handleCustomColorLoad(
                        location.state.loggerData,
                        location.state.serial,
                        loggersTemp,
                        null,
                        setErrorMessage,
                        setLoggers,
                        intl
                    )
                } else {
                    setLoggers(loggersTemp)
                }
            }

            
        }
        setShowShowHide(!showShowHide)
    }

    return (
        
        <div> 
            {!isLandscape &&
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
                    <div>
                        <h4 style={{ textAlign: "center" }}><b><FormattedMessage id="Please rotate your screen" /></b></h4>
                        <h4 style={{ textAlign: "center" }}><b><FormattedMessage id="to view the graph" /></b></h4>
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <Lottie animationData={rotateScreenAnimation} loop={true} style={{ width: "50%" }} />
                        </div>
                        <div style={{ width: "100%", marginTop: 80 }}>
                            <FullWidthMobileButton disabled={false} text={'Back'} onClick={(): void => history.goBack()} color="error" />
                        </div>

                    </div>

                </div>
            }
            {isLandscape &&
                <div>
                    {loading &&
                        <div style={{
                            position: 'absolute', left: 'Calc(50% - 100px)', top: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 13000
                        }}>
                            <Loader color={undefined} />
                        </div>
                    }

                    {!loading && 
                        <div style={{ position: 'relative' }}>
                            <div style={{ background: Ashridge, width: "100vw", height: 55, transform: isTablet ? "translate(-3.5vw, -3vh)" : "translate(-3.5vw, -8.5vh)", display: "flex", justifyContent: "space-evenly", alignItems: "center" }}>
                                <div style={{ width: "150px" }}>
                                    <FullWidthMobileButton disabled={false} text={intl.formatMessage({ id: 'Back' })} onClick={(): void => history.goBack()} color="error" />
                                </div>
                                <div style={{ width: "200px" }}>
                                    <FullWidthMobileButton disabled={false} text={intl.formatMessage({ id: 'change date' })} onClick={(): void => { toggleDatePicker() }} color="primary" />
                                </div>
                                <div style={{ width: "200px" }}>
                                    <FullWidthMobileButton disabled={false} text={intl.formatMessage({ id: 'show / hide' })} onClick={(): void => { toggleShowHideClick() }} color="secondary" />
                                </div>

                            </div>
                            {showDatePicker &&
                                <SlideInAnimation direction="top" duration={1.2} delay={1} transitionAmount={400} isVisible={showDatePicker}>
                                    <div style={{
                                        background: Ashridge,
                                        width: "100vw",
                                        transform: isTablet ? "translate(-2.8vw, -3vh)" : "translate(-3.5vw, -8vh)",
                                        display: "block",
                                        justifyContent: "space-evenly",
                                        alignItems: "center",
                                        position: "absolute",
                                        color: "white",
                                        padding: "10px"

                                    }}>
                                        <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                                            <DateRange startDate={startDate} endDate={endDate} rainfall={false} onDateChange={onDateChange} type="date" showButton={dateChange} handleRedrawClick={handleRedraw} />
                                        </div>
                                        

                                    </div>
                                </SlideInAnimation>
                            }
                            {showShowHide &&
                                <SlideInAnimation direction="top" duration={1.2} delay={1} transitionAmount={400} isVisible={showShowHide}>
                                    <div style={{
                                        background: Ashridge,
                                        width: "100vw",
                                        transform: isTablet ? "translate(-2.8vw, -3vh)" : "translate(-3.5vw, -8vh)",
                                        display: "flex",
                                        justifyContent: "space-evenly",
                                        alignItems: "center",
                                        position: "absolute",
                                        color: "white",
                                        padding: "10px"
                                    }}>
                                        {location.state.serial &&
                                            <div style={{width: 200} }>
                                                <FullWidthMobileButton
                                                color={loggers.map(l => l.serial).includes(location.state.serial) ? 'secondary' : 'error'}
                                                disabled={false}
                                                text={location.state.serial.toString()}
                                                onClick={(): void => handleShowHide(location.state.serial.toString())}
                                                />
                                            </div>
                                        }
                                        <div style={{ width: 200 }}>
                                            <FullWidthMobileButton
                                                color={showAvg ? 'secondary' : 'error'}
                                                disabled={false}
                                                text={intl.formatMessage({ id: '7 Day Avg' })}
                                                onClick={():void => handleShowHide('avg')}
                                            />
                                        </div>
                                    </div>
                                </SlideInAnimation>
                            }
                            {graphData?.length > 0 &&
                                loggers && <Chart
                                    height={isTablet ? 700 : 300}
                                    dataSource={graphData}
                                    ref={chartRef}
                                >
                                    <CommonSeriesSettings
                                        argumentField="day"
                                        ignoreEmptyPoints={false}
                                    //type="stackedbar"
                                    >
                                        <Point size={15} />
                                        <Font weight={900} size={40} />
                                    </CommonSeriesSettings>

                                    <ValueAxis
                                        width={2}
                                        color={loggers.length > 1 ? '#767676' : loggers[0] ? loggers[0].color : professionalColors[0]}
                                        title={{ text: intl.formatMessage({ id: "Minimum Night Flow in l/s" }), font: { color: loggers.length > 1 ? '#767676' : loggers[0] ? loggers[0].color : professionalColors[0] , size: 12 } }}

                                    >
                                        <Label font={{ color: loggers.length > 1 ? '#767676' : loggers[0] ? loggers[0].color : professionalColors[0] }} />
                                    </ValueAxis>
                                    <CommonAxisSettings
                                        valueMarginsEnabled={true}
                                        minValueMargin={0.1}    //add extra 10% to show a 'gap'
                                        maxValueMargin={0.1}     //add extra 10% to show a 'gap'
                                    />
                                    
                                    {loggers && loggers.length > 0 && loggers.map(logger => {
                                        return renderLoggerSeries(logger)
                                    })}
                                    {showAvg && <Series
                                        valueField="average"
                                        color={averageStyling.color}
                                        name={intl.formatMessage({ id: "7 Day Average" })}
                                        type="spline"
                                        width={averageStyling.width}
                                        point={{ visible: averageStyling.showPoints, size: averageStyling.size }}
                                    />}

                                    <ArgumentAxis
                                        width={2}
                                        allowDecimals={false}
                                        argumentType="datetime"
                                        minValueMargin={0.01}
                                        maxValueMargin={0.01}
                                        aggregationInterval="day"
                                        visualRange={visualRange}

                                    >
                                        <GridChart visible={true} />
                                    </ArgumentAxis>
                                    <Legend
                                        visible={false} />

                                    <Crosshair
                                        enabled={true}
                                        color="#949494"
                                        width={3}
                                        dashStyle="dot"
                                    >
                                        <Label
                                            visible={true}
                                            backgroundColor="#949494"
                                        >
                                            <Font
                                                color="#fff"
                                                size={14}
                                            />
                                        </Label>
                                    </Crosshair>
                                    <ZoomAndPan
                                        allowMouseWheel={false}
                                        argumentAxis="both"
                                        valueAxis="none"
                                        dragToZoom={true}
                                    />
                                    <Tooltip enabled={true} contentRender={(data): React.ReactNode => renderToolTip(data, intl)} />
                                </Chart>}
                            {allValuesZero && <p style={{ position: 'absolute', top: 200, left: 250 }}><FormattedMessage id="Minimum Night Flow is continuously 0 l/s" /></p>}
                        </div>
                    }
                </div>
            }
                
        </div>
    )
}
export default MinimumNightFlowMobile